import {
  faChevronDown,
  faSearch,
  faDownload,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import Button from 'components/Button'
import Dropdown from 'components/Dropdown'
import { TRANSACTION_TYPES } from 'constants/filters'
import { Form, Formik } from 'formik'

import React, { PropsWithChildren, useCallback, useState } from 'react'
import DatePicker from 'react-datepicker'
import { useParams, NavLink } from 'react-router-dom'
import { formatDateToShort } from 'services/helpers'
import { DropDownItem } from 'types/components'

import useTransactionFilter from './useTransactionFilter'
import useTransactionsMerchantLookup from './useTransactionsMerchantLookup'
import { useCanUI } from 'hooks/useCanUI'

type DatePickerProps = React.InputHTMLAttributes<HTMLInputElement> & {
  value?: string
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}

function Transactions(): React.ReactElement {
  const { isMerchantTransactionDownload } = useCanUI()
  const { traderId } = useParams()
  const { merchantLookupDropdownOptions } = useTransactionsMerchantLookup({
    memberTraderId: String(traderId),
  })
  const {
    handleTransactionFilter,
    handleTransactionFilterClear,
    setFilterStartDate,
    setFilterEndDate,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedMerchant,
    setSelectedMerchant,
    initialValues,
    isFilteredTransactions,
    transactionsLoading,
    deviceOptions,
    transactionTypeId,
  } = useTransactionFilter({ merchantTraderId: String(traderId) })

  const [submitError, setSubmitError] = useState<string | null>(null)
  const downloadSearchParams = new URLSearchParams()
  if (startDate && endDate) {
    downloadSearchParams.set('startDate', formatDateToShort(startDate))
    downloadSearchParams.set('endDate', formatDateToShort(endDate))
  }
  if (transactionTypeId)
    downloadSearchParams.set('transactionTypeId', String(transactionTypeId))
  if (deviceOptions)
    downloadSearchParams.set('deviceOptions', String(deviceOptions))

  //  start: Datepicker and formatting
  const onChangeDatepicker = (dates: [Date, Date] | null): void => {
    let start
    let end
    if (dates === null) {
      start = null
      end = null
      setFilterStartDate('')
      setFilterEndDate('')
    } else {
      start = dates[0]
      end = dates[1]
      setFilterEndDate('')
    }
    setStartDate(start)
    setEndDate(end)
    setSubmitError(null)
  }

  const formatDate = (): string => {
    let dateString = ''
    if (startDate) {
      dateString = startDate.toLocaleDateString()
    }
    if (endDate) {
      dateString = dateString + ' - ' + endDate.toLocaleDateString()
    }
    return dateString || 'All'
  }

  const DatePickerInput = ({
    value,
    onClick,
  }: PropsWithChildren<DatePickerProps>): JSX.Element => (
    <button
      className="p-2 pr-10 text-left border border-gray-400"
      style={{ minWidth: '120px' }}
      onClick={onClick}
      type="button"
    >
      {formatDate()}
      {!value && (
        <Icon
          icon={faChevronDown}
          className="absolute top-0 right-0 h-full ml-4 mr-2"
        />
      )}
    </button>
  )

  // start: Merchant lookup dropdown options handler
  const merchantLoadOptions = useCallback(
    async (inputValue: string): Promise<DropDownItem[]> => {
      return merchantLookupDropdownOptions(inputValue)
    },
    [merchantLookupDropdownOptions]
  )

  const handleSubmitWrapper = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (values: any, actions: any) => {
      const hasDevice =
        values.deviceCode !== null &&
        values.deviceCode !== undefined &&
        values.deviceCode !== ''
      const hasStartOrEnd = !!startDate || !!endDate
      if (hasDevice && !hasStartOrEnd) {
        setSubmitError('Please select a date range when filtering by device.')
        actions.setSubmitting(false)
        return
      }
      setSubmitError(null)
      await handleTransactionFilter(values, actions)
    },
    [handleTransactionFilter, startDate, endDate]
  )

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmitWrapper}>
      {({ values, setFieldValue }) => (
        <Form>
          <div className="flex flex-wrap items-end mt-4 mb-5 gap-2">
            <div className="flex flex-col gap-2">
              <p>
                <span className="tracking-wider font-light">Date Range</span>
              </p>
              <DatePicker
                title="Date Picker"
                name="dateRange"
                onChange={onChangeDatepicker}
                selected={startDate}
                startDate={startDate}
                endDate={endDate}
                className="p-2 border border-gray-400"
                placeholderText="All"
                customInput={<DatePickerInput />}
                selectsRange
                isClearable
              />
            </div>
            <Dropdown
              title="Transaction Type"
              value={values.transactionType}
              options={TRANSACTION_TYPES}
              onSelect={(option: DropDownItem) => {
                setFieldValue('transactionType', option)
              }}
              className="w-40"
              borderColor="#C4C4C4"
            />

            <Dropdown
              title="Device Code"
              value={
                values.deviceCode
                  ? deviceOptions.find(
                      (o) => o.value === String(values.deviceCode)
                    ) || null
                  : null
              }
              options={deviceOptions}
              onSelect={(option: DropDownItem | null) => {
                setFieldValue(
                  'deviceCode',
                  option ? Number(option.value) : null
                )
                setSubmitError(null)
              }}
              className="w-40"
              borderColor="#C4C4C4"
              placeholder="All devices"
            />

            <Button
              type="submit"
              icon={faSearch}
              className="ml-1 rounded-lg"
              iconRight={false}
              disabled={transactionsLoading ? true : false}
            >
              <span className="tracking-wider font-bold">Filter</span>
            </Button>

            {isMerchantTransactionDownload && (
              <Button
                type="button"
                icon={faDownload}
                className="ml-1 rounded-lg"
                iconRight={false}
              >
                <NavLink
                  to={`/reports/${traderId}/merchant-transactions-reports?${downloadSearchParams}`}
                >
                  <span className="tracking-wider font-bold">Download</span>
                </NavLink>
              </Button>
            )}

            {submitError && (
              <div className="text-sm text-red ml-1" role="alert">
                {submitError}
              </div>
            )}

            {isFilteredTransactions ? (
              <Button
                type="reset"
                className="ml-2 rounded-lg"
                iconRight={false}
                onClick={handleTransactionFilterClear}
                disabled={transactionsLoading ? true : false}
              >
                <span className="tracking-wider font-bold">Clear</span>
              </Button>
            ) : null}
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default Transactions
